/**
 * Determines if a star should be selected based on index and rating values
 *
 * @param index - The zero-based index of the star
 * @param selectedValue - The currently selected rating value
 * @returns Whether the star should be displayed as selected
 */
export const isStarSelected = (
  index: number,
  selectedValue: number,
): boolean => {
  return index <= selectedValue - 1;
};

/**
 * Gets the label for a star based on its index
 *
 * @param index - The zero-based index of the star
 * @param labels - Array of labels for stars
 * @returns The label for the star
 */
export const getStarLabel = (index: number, labels?: string[]): string => {
  if (!labels || !Array.isArray(labels) || !labels[index]) {
    return `${index + 1} star${index > 0 ? 's' : ''}`;
  }
  return labels[index];
};

/**
 * Focuses a star button by its value
 *
 * @param containerElement - The container element that holds the star buttons
 * @param name - The base name for the star buttons
 * @param value - The value of the star to focus
 * @returns Whether the focus was successful
 */
export const focusStarButton = (
  containerElement: HTMLElement | null,
  name: string,
  value: number,
): boolean => {
  if (!containerElement) return false;

  try {
    const button = containerElement.querySelector(
      `#${name}-${value}`,
    ) as HTMLButtonElement;
    if (button) {
      button.focus();
      return true;
    }
  } catch (error) {
    console.error('Error focusing star button:', error);
  }

  return false;
};

/**
 * Validates if a rating value is within allowed bounds
 *
 * @param value - The rating value to validate
 * @param min - The minimum allowed value (defaults to 0)
 * @param max - The maximum allowed value (defaults to 5)
 * @returns Whether the value is valid
 */
export const isValidRating = (value: number, min = 0, max = 5): boolean => {
  return !Number.isNaN(value) && value >= min && value <= max;
};
